import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Autocomplete,
  Button,
  IconButton,
  Tooltip,
  styled,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  People as PeopleIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { useNavigate } from 'react-router-dom';
import { fetchUsers, fetchUsersCount, createUser } from '../api/features/users/usersApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { usePermissions } from '../api/auth/PermissionsContext';
import { fetchClients } from '../api/features/clientData/clientApi';
import { SAPIconButton, PageLayout, FilterBar } from '../components';
import { SAP_COLORS } from '../theme/sapDesignSystem';

const ROWS_PER_PAGE = 10;

const createModalFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'background.paper',
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
  '& .MuiInputLabel-root': { color: 'text.secondary' },
  '& .MuiInputBase-input': { color: 'text.primary' },
};

// Removed styled components - using sx props with Material-UI theme instead

export default function Users() {
  const navigate = useNavigate();
  const { clientCode } = useClientCode();
  const { permissions } = usePermissions();
  const isAdmin = clientCode === 'admin';

  // State
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [clientOptions, setClientOptions] = useState<any[]>([]);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    userName: '',
    password: '',
    confirmPassword: '',
    clientId: null,
    active: true,
  });

  // Fetch client options for combobox
  useEffect(() => {
    const fetchClientOptions = async () => {
      try {
        logger.log('Fetching clients for clientCode:', clientCode);
        logger.log('isAdmin:', isAdmin);
        logger.log('permissions:', permissions);
        
        // Try the main fetchClients function first
        let clients = await fetchClients({ clientCode, firstResult: -1, maxResult: 0 });
        logger.log('Fetched clients from fetchClients:', clients);
        
        // If that fails, try fetchAllClients as fallback
        if (!clients || !Array.isArray(clients) || clients.length === 0) {
          logger.log('Trying fetchAllClients as fallback...');
          const { fetchAllClients } = await import('../api/features/clientData/clientApi');
          clients = await fetchAllClients(clientCode);
          logger.log('Fetched clients from fetchAllClients:', clients);
        }
        
        if (Array.isArray(clients)) {
          const options = clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code }));
          logger.log('Client options:', options);
          setClientOptions(options);
        } else {
          logger.warn('Clients response is not an array:', clients);
          setClientOptions([]);
        }
      } catch (err) {
        logger.error('Failed to fetch clients:', err);
        setClientOptions([]);
      }
    };
    
    // Always fetch clients, not just for admins
    fetchClientOptions();
  }, [clientCode]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, countData] = await Promise.all([
        fetchUsers({
          clientCode,
          firstResult: page * ROWS_PER_PAGE,
          maxResult: ROWS_PER_PAGE,
          clientId: selectedClient?.value,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: email || undefined,
        }),
        fetchUsersCount({
          clientCode,
          clientId: selectedClient?.value,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: email || undefined,
        }),
      ]);

      setUsers(usersData);
      setRowCount(countData || 0);
      setHasSearched(true);
    } catch (err) {
      logger.error('Error fetching users:', err);
      setError('Failed to fetch users');
      setUsers([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const handleReset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setSelectedClient(null);
    setPage(0);
    setHasSearched(false);
    setUsers([]);
    setRowCount(0);
    setError(null);
  };

  const [creating, setCreating] = useState(false);

  const handleCreateUser = async () => {
    const { firstName, lastName, email, userName, password, confirmPassword, clientId } = newUserData;
    if (!firstName?.trim() || !lastName?.trim() || !email?.trim() || !userName?.trim() || !password || !confirmPassword) {
      toast.error('Please fill all required fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Password and Confirm Password do not match');
      return;
    }
    if (!clientId) {
      toast.error('Please select a client');
      return;
    }
    setCreating(true);
    try {
      await createUser({
        clientCode,
        payload: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          userName: userName.trim(),
          password,
          confirmPassword,
          clientId,
          isActive: newUserData.active,
        },
      });
      toast.success('User created successfully!');
      setCreateDialogOpen(false);
      setNewUserData({
        firstName: '',
        lastName: '',
        email: '',
        userName: '',
        password: '',
        confirmPassword: '',
        clientId: null,
        active: true,
      });
      if (hasSearched) fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (user: any) => {
    navigate(`/userEdit/${user.id}`);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Effect for pagination - fetch data when page changes
  useEffect(() => {
    if (hasSearched) {
      fetchData();
    }
  }, [page]);

  return (
    <PageLayout
      title="User Management"
      description="Search and manage user accounts and permissions"
      titleSx={{ color: SAP_COLORS.sapBlue }}
    >
      <FilterBar loading={loading}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: isAdmin ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr' }, 
          gap: 2,
          alignItems: 'end',
          width: '100%',
        }}>
          {/* First Name Input */}
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Enter first name"
            size="small"
            fullWidth
          />
          
          {/* Last Name Input */}
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Enter last name"
            size="small"
            fullWidth
          />
          
          {/* Email Input */}
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
            size="small"
            fullWidth
          />
          
          {/* Client Dropdown - Hidden for non-admin */}
          {isAdmin && (
            <Autocomplete
              options={clientOptions}
              value={selectedClient}
              onChange={(_, v) => setSelectedClient(v)}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  size="small"
                  placeholder={clientOptions.length > 0 ? "Select client" : "No clients available"}
                  fullWidth
                />
              )}
              disabled={loading}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              noOptionsText={clientOptions.length === 0 ? "No clients found" : "No options"}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SAPIconButton
            icon="search"
            variant="primary"
            onClick={handleSearch}
            disabled={loading}
            aria-label="Search"
          />
          <SAPIconButton
            icon="reset"
            variant="secondary"
            onClick={handleReset}
            disabled={loading}
            aria-label="Reset"
          />
          <SAPIconButton
            icon="add"
            variant="success"
            onClick={() => setCreateDialogOpen(true)}
            disabled={loading}
            aria-label="Create User"
          />
        </Box>
      </FilterBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {/* Data Card */}
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: 'primary.main' }} size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: 'error.main' + '10',
                color: 'error.main',
                border: `1px solid ${'error.main'}30`,
              }}>
                {error}
              </Alert>
            </Box>
          ) : !hasSearched ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Please use the search filters above to view users.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  {/* SAP Fiori Table Header */}
                  <TableHead sx={{
          backgroundColor: 'background.default',
          '& .MuiTableCell-head': {
            fontWeight: 'semibold',
            color: 'text.secondary',
            borderBottom: '2px solid',
            borderBottomColor: 'primary.main',
          },
        }}>
                    <TableRow>
                      <TableCell sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 'semibold',
                        width: '80px'
                      }}>
                        S.No.
                      </TableCell>
                      <TableCell sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 'semibold' 
                      }}>
                        First Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 'semibold' 
                      }}>
                        Last Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 'semibold' 
                      }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 'semibold',
                        width: '100px'
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  
                  {/* Table Body */}
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ 
                          color: 'text.secondary',
                          py: 4,
                          fontWeight: 'medium',
                        }}>
                          No users found. Try searching with different criteria.
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user, idx) => (
                        <TableRow
                          key={user.id}
                          sx={{
                            bgcolor: idx % 2 === 0 ? 'background.paper' : 'background.default',
                            '&:hover': { 
                              bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: 'text.secondary', 
                            fontWeight: 'medium' 
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: 'text.primary',
                            fontWeight: 'medium',
                          }}>
                            {user.firstName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ 
                            color: 'text.primary',
                            fontWeight: 'medium',
                          }}>
                            {user.lastName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ 
                            color: 'text.primary',
                            fontWeight: 'medium',
                          }}>
                            {user.email || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Tooltip title="Edit User" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleEditClick(user)}
                                  sx={{ 
                                    color: 'primary.main',
                                    '&:hover': { 
                                      backgroundColor: 'action.hover',
                                    },
                                  }}
                                  aria-label="Edit User"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {users.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + users.length, rowCount)} of {rowCount} users
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handlePageChange(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog - 3 sections: header, content, footer */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: (theme) => theme.shadows[8],
            maxHeight: { xs: '95vh', sm: '90vh' },
            m: { xs: 1.5, sm: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 'auto', sm: 400 },
          },
        }}
      >
        {/* Section 1: Header - clear bottom margin */}
        <DialogTitle
          sx={{
            flexShrink: 0,
            pt: 2,
            pb: 2.5,
            px: { xs: 2, sm: 3 },
            mb: 0,
            bgcolor: 'background.default',
            color: 'text.primary',
            fontWeight: 600,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          Create New User
        </DialogTitle>

        {/* Section 2: Main content - 6 inputs in 3×2 grid */}
        {/* MUI sets paddingTop:0 when DialogContent follows DialogTitle - override it */}
        <DialogContent
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            px: { xs: 2, sm: 3 },
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            // Force gap below header - MUI removes it by default
            paddingTop: '32px !important',
            paddingBottom: '32px !important',
          }}
        >
          <Box
            component="form"
            autoComplete="off"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, sm: 2.5, md: 3 },
              minWidth: 0,
            }}
          >
            <TextField
              label="First Name"
              value={newUserData.firstName}
              onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
              placeholder="Enter first name"
              fullWidth
              size="small"
              autoComplete="off"
              sx={createModalFieldSx}
            />
            <TextField
              label="Last Name"
              value={newUserData.lastName}
              onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
              placeholder="Enter last name"
              fullWidth
              size="small"
              autoComplete="off"
              sx={createModalFieldSx}
            />
            <TextField
              label="Email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              placeholder="Enter email address"
              fullWidth
              size="small"
              type="email"
              autoComplete="off"
              sx={createModalFieldSx}
            />
            <TextField
              label="Username"
              value={newUserData.userName}
              onChange={(e) => setNewUserData({ ...newUserData, userName: e.target.value })}
              placeholder="Enter username"
              fullWidth
              size="small"
              autoComplete="off"
              sx={createModalFieldSx}
            />
            <TextField
              label="Password"
              type="password"
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              placeholder="Enter password"
              fullWidth
              size="small"
              autoComplete="new-password"
              sx={createModalFieldSx}
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={newUserData.confirmPassword}
              onChange={(e) => setNewUserData({ ...newUserData, confirmPassword: e.target.value })}
              placeholder="Confirm password"
              fullWidth
              size="small"
              autoComplete="new-password"
              sx={createModalFieldSx}
            />
            <Autocomplete
              options={clientOptions}
              value={clientOptions.find((c) => c.value === newUserData.clientId) || null}
              onChange={(_, v) => setNewUserData({ ...newUserData, clientId: v?.value || null })}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  placeholder="Select client"
                  size="small"
                  autoComplete="off"
                  sx={createModalFieldSx}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              slotProps={{
                popper: {
                  sx: { zIndex: 9999 },
                  disablePortal: false,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newUserData.active}
                  onChange={(e) => setNewUserData({ ...newUserData, active: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
              sx={{
                gridColumn: { xs: '1', sm: '1 / -1', md: '1 / -1' },
                alignSelf: 'center',
                m: 0,
              }}
            />
          </Box>
        </DialogContent>

        {/* Section 3: Footer - action buttons, clear top separation */}
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2.5,
            gap: 1.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.default',
          }}
        >
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateUser}
            variant="contained"
            disabled={creating}
            sx={{ 
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.main' + 'dd' },
              color: 'background.paper',
            }}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
} 